/**
 * JSON requests to the site's API with a timeout, cancellation and the
 * same-origin header every state-changing request must carry
 * (`X-Requested-With: docszone`, see app/ARCHITECTURE.md, CSRF policy).
 *
 * Failures are typed: ApiError (the server answered with an error),
 * NetworkError (no answer or a timeout). A caller's AbortSignal cancels the
 * request with the browser's AbortError, which is not a failure.
 */

export const REQUESTED_WITH = "docszone";
const DEFAULT_TIMEOUT_MS = 30_000;

const GENERIC_MESSAGE = "Something went wrong. Please try again.";

export class ApiError extends Error {
  override name = "ApiError";
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }

  /** The error of an API answer (`{"error": {"code", "message"}}`, or anything else). */
  static fromBody(status: number, body: unknown): ApiError {
    const error = isRecord(body) ? body["error"] : undefined;
    if (isRecord(error)) {
      const code = typeof error["code"] === "string" ? error["code"] : "error";
      const message =
        typeof error["message"] === "string" && error["message"] !== ""
          ? error["message"]
          : GENERIC_MESSAGE;
      return new ApiError(status, code, message);
    }
    return new ApiError(status, `http_${status}`, messageForStatus(status));
  }
}

export class NetworkError extends Error {
  override name = "NetworkError";
}

export class InvalidResponseError extends Error {
  override name = "InvalidResponseError";
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

function messageForStatus(status: number): string {
  if (status === 413) return "The file is too large.";
  if (status === 429) return "Too many requests. Please try again later.";
  if (status >= 500) {
    return "The service is temporarily unavailable. Please try again later.";
  }
  return GENERIC_MESSAGE;
}

/** The message to show a visitor for any failure of an API call. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof NetworkError) {
    return error.message;
  }
  return GENERIC_MESSAGE;
}

interface RequestOptions {
  readonly body?: unknown;
  readonly signal?: AbortSignal | undefined;
  readonly timeoutMs?: number;
}

class TimeoutReason extends Error {
  override name = "TimeoutReason";
}

/** Send a JSON request; resolves with the parsed body (null for 204). */
export async function requestJson(
  method: "GET" | "POST" | "DELETE",
  url: string,
  options: RequestOptions = {},
): Promise<unknown> {
  const controller = new AbortController();
  const cancel = (): void => {
    controller.abort(options.signal?.reason);
  };
  if (options.signal?.aborted === true) cancel();
  options.signal?.addEventListener("abort", cancel, { once: true });
  const timer = setTimeout(() => {
    controller.abort(new TimeoutReason());
  }, options.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  try {
    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          "X-Requested-With": REQUESTED_WITH,
          ...(options.body === undefined
            ? {}
            : { "Content-Type": "application/json" }),
        },
        body: options.body === undefined ? null : JSON.stringify(options.body),
        credentials: "same-origin",
        cache: "no-store",
        signal: controller.signal,
      });
    } catch (error) {
      throw toRequestError(error, controller.signal);
    }
    if (response.status === 204) return null;
    let body: unknown = null;
    try {
      body = await response.json();
    } catch (error) {
      if (controller.signal.aborted) {
        throw toRequestError(error, controller.signal);
      }
      body = null; // not JSON (a proxy error page): judged by the status below
    }
    if (!response.ok) throw ApiError.fromBody(response.status, body);
    return body;
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener("abort", cancel);
  }
}

function toRequestError(error: unknown, signal: AbortSignal): unknown {
  if (signal.aborted && signal.reason instanceof TimeoutReason) {
    return new NetworkError(
      "The server did not answer in time. Please try again.",
      {
        cause: error,
      },
    );
  }
  if (signal.aborted) {
    return new DOMException("The request was cancelled", "AbortError");
  }
  return new NetworkError(
    "Internet connection problems. Please check your connection and try again.",
    { cause: error },
  );
}
